import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Search, Camera, Clapperboard, Mail } from "lucide-react";
import { api } from "../lib/api";
import { COUNTRIES, HUBS } from "../constants/options";
import { CountryPicker } from "../components/CountryPicker";
import { ExtraCard } from "../components/ExtraCard";
import { PricingCard } from "../components/PricingCard";
import { Announcement } from "../components/Announcement";
import { LatestCalls } from "../components/LatestCalls";

const HINTS = { "United Kingdom": "Postcode or town (e.g. M1 1AE, Leeds)", "United States": "ZIP or city (e.g. 90028, Atlanta)", Ireland: "Town or area (e.g. Dublin 2, Galway)" };

const HeroSearch = () => {
  const [location, setLocation] = useState("");
  const [country, setCountry] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (country) qs.set("country", country);
    if (location.trim()) {
      qs.set("near", location.trim());
      qs.set("radius", "25");
    }
    navigate(`/search${qs.toString() ? `?${qs}` : ""}`);
  };
  return (
    <div className="mt-10 max-w-xl fade-up" style={{ animationDelay: "300ms" }}>
      <CountryPicker value={country} onChange={setCountry} className="bg-black/60 backdrop-blur-xl border-white/20 border-b-0" testId="hero-country" />
      <form onSubmit={submit} className="flex flex-col sm:flex-row border border-white/20 bg-black/60 backdrop-blur-xl" data-testid="hero-search-form">
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={HINTS[country] || "Postcode, ZIP or town (e.g. M1 1AE, 90028, Dublin)"}
        className="flex-1 bg-transparent px-5 py-4 outline-none placeholder:text-white/40 focus:ring-2 focus:ring-primary"
        data-testid="hero-search-input"
      />
      <button className="bg-primary text-primary-foreground px-6 py-4 font-bold uppercase text-sm tracking-wider flex items-center justify-center gap-2 hover:bg-[hsl(43,100%,60%)] transition-colors" data-testid="hero-search-button">
        <Search size={16} /> Find extras
      </button>
      </form>
    </div>
  );
};

const Hero = () => (
  <section className="relative min-h-[88vh] flex items-end overflow-hidden" data-testid="hero-section">
    <img src="/images/hero_background.jpg" alt="Film crew on set" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-black/50" />
    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[hsl(0,0%,2%)] to-transparent" />
    <div className="relative max-w-7xl mx-auto w-full px-6 md:px-10 pb-20 md:pb-28">
      <p className="label-xs text-primary fade-up">TV & film background artists · UK · USA · Ireland</p>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold leading-[0.92] mt-5 max-w-4xl text-shadow fade-up" style={{ animationDelay: "120ms" }}>
        The faces behind<br />every great <span className="text-primary">scene.</span>
      </h1>
      <p className="mt-6 max-w-lg text-white/80 text-base md:text-lg text-shadow fade-up" style={{ animationDelay: "200ms" }}>
        Casting a crowd in the UK, USA or Ireland? Search local extras by area. Want to be on set? Create your own page at extras.tv/yourname — first 6 months FREE, then £10 / $14 / €12 a year.
      </p>
      <HeroSearch />
    </div>
  </section>
);

const STEPS = [
  { icon: Clapperboard, title: "Sign up", text: "Create your account in under a minute." },
  { icon: Camera, title: "Build your profile", text: "Add photos, your area, playing age, height and skills." },
  { icon: Mail, title: "Get booked", text: "Casting teams find you by area and get in touch." },
];

const HowItWorks = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12" data-testid="how-it-works-section">
    <div className="md:col-span-5">
      <p className="label-xs">For extras</p>
      <h2 className="font-display text-3xl md:text-5xl font-bold mt-4 leading-none">Get seen by the people casting crowds</h2>
      <p className="text-white/60 mt-6 max-w-md">One simple yearly fee. No agency commission. You decide whether your contact details are public or kept private behind a message form.</p>
      <Link to="/register" className="inline-flex items-center gap-2 mt-8 text-primary font-bold uppercase text-sm tracking-wider group" data-testid="how-join-link">
        Create your profile <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
    <div className="md:col-span-7 grid sm:grid-cols-3 border border-white/10">
      {STEPS.map((s, i) => (
        <div key={s.title} className="p-8 border-b sm:border-b-0 sm:border-r last:border-0 border-white/10">
          <span className="font-display text-5xl text-white/10">0{i + 1}</span>
          <s.icon className="text-primary mt-6" size={24} />
          <h3 className="font-display text-xl mt-4">{s.title}</h3>
          <p className="text-sm text-white/60 mt-2">{s.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Showcase = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-12 gap-4 md:gap-6" data-testid="showcase-section">
    <div className="col-span-12 md:col-span-8 relative aspect-[16/10] overflow-hidden group">
      <img src="/images/talent_female_1.jpg" alt="Example headshot" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute bottom-0 left-0 p-8 bg-gradient-to-t from-black/80 w-full">
        <p className="label-xs text-primary">For productions</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Search by area. Fill the frame.</h2>
      </div>
    </div>
    <div className="col-span-12 md:col-span-4 grid grid-rows-2 gap-4 md:gap-6">
      <img src="/images/talent_male_1.jpg" alt="Example headshot" className="w-full h-full object-cover aspect-[4/3] md:aspect-auto" />
      <div className="bg-primary text-primary-foreground p-8 flex flex-col justify-between">
        <p className="font-display text-3xl font-bold leading-tight">Every region. Every look. Every age.</p>
        <Link to="/search" className="inline-flex items-center gap-2 font-bold uppercase text-sm tracking-wider mt-6 group" data-testid="showcase-search-link">
          Browse extras <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

const Regions = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="regions-section">
    <p className="label-xs">Filming locations</p>
    <h2 className="font-display text-3xl md:text-5xl font-bold mt-4 leading-none">UK. USA. Ireland.</h2>
    <div className="grid md:grid-cols-3 border border-white/10 mt-10">
      {COUNTRIES.map((c) => (
        <div key={c.name} className="p-8 border-b md:border-b-0 md:border-r last:border-0 border-white/10" data-testid={`country-col-${c.short.toLowerCase()}`}>
          <div className="flex items-baseline justify-between">
            <Link to={`/${c.slug}`} className="font-display text-2xl hover:text-primary transition-colors" data-testid={`country-heading-${c.slug}`}>{c.name}</Link>
            <span className="label-xs">{c.price} / yr</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            {HUBS[c.name].map((r) => (
              <Link
                key={r}
                to={`/search?country=${encodeURIComponent(c.name)}&region=${encodeURIComponent(r)}`}
                className="border border-white/15 px-3 py-1.5 text-sm hover:border-primary hover:text-primary transition-colors"
                data-testid={`region-chip-${c.short.toLowerCase()}-${r.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {r}
              </Link>
            ))}
          </div>
          <Link to={`/${c.slug}`} className="inline-flex items-center gap-2 mt-6 text-primary text-sm font-bold uppercase tracking-wider group" data-testid={`country-all-${c.short.toLowerCase()}`}>
            Extras in {c.the} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      ))}
    </div>
  </section>
);

const Featured = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/extras/featured").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="featured-section">
      <p className="label-xs">Recently updated</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        {items.map((e, i) => <ExtraCard key={e.slug} extra={e} index={i} />)}
      </div>
    </section>
  );
};

const Pricing = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center" data-testid="pricing-section">
    <div className="md:col-span-6">
      <p className="label-xs">Membership · first 6 months FREE</p>
      <h2 className="font-display text-3xl md:text-5xl font-bold mt-4 leading-none">Your own web address. Your photos. Your story.</h2>
      <p className="text-white/60 mt-6 max-w-md">Create your own page at <span className="text-white font-semibold">extras.tv/yourname</span>, add your photos and a few words about yourself, then promote yourself across Facebook groups, WhatsApp and social media so casting teams can find you.</p>
      <p className="text-white/60 mt-4 max-w-md"><span className="text-primary font-bold">Your first 6 months are FREE.</span> After that it's just £10 (or $14 / €12) a year to keep your page live. Nothing is taken until the 6 months are up, and you can cancel any time. Searching is always free for productions.</p>
    </div>
    <div className="md:col-span-5 md:col-start-8">
      <PricingCard note="Also payable as $14 USD or €12 EUR.">
        <Link to="/register" className="block text-center bg-primary text-primary-foreground py-4 font-bold uppercase tracking-wider text-sm hover:bg-[hsl(43,100%,60%)] transition-colors" data-testid="pricing-join-button">
          Get listed
        </Link>
      </PricingCard>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <div className="grain" />
      <Announcement />
      <Hero />
      <LatestCalls />
      <HowItWorks />
      <Showcase />
      <Regions />
      <Featured />
      <Pricing />
    </div>
  );
}
